/**
 * wsOverflowRecovery — shared "payload overflow" close-event detector.
 *
 * Why this exists
 * ────────────────
 * Both `orgYDocStore` and `conversationYjsStore` own a local Y.Doc room that
 * the server's per-prefix payload budget can reject (close code 1008). In
 * practice the `ws` library usually terminates the socket before the 1008
 * close frame is delivered, so the client typically observes 1006 (abnormal
 * close) instead and has to infer overflow from a pattern: two consecutive
 * 1006 closes within a short window.
 *
 * A plain "is the doc non-empty" heuristic produces false positives: any
 * normal room (a modest org tree, a short conversation) is non-empty but
 * nowhere near its payload budget. Chronic connection instability unrelated
 * to payload size (flaky network, LB idle timeouts, auth-refresh races)
 * would then trip recovery and destroy an already-synced local doc every
 * ~10-15s. Gating on the doc's actual encoded size makes the guard
 * evidence-based: only a doc that could plausibly explain a real overflow
 * triggers recovery; small docs ride out transient 1006s via the provider's
 * own reconnect/backoff.
 *
 * `orgYDocStore` already carried this size-gated fix (originally written to
 * cure exactly this false-positive pattern in production). This module lifts
 * it out so `conversationYjsStore` — which had the same open-ended
 * "non-empty" heuristic — gets an identical, independently-verified fix
 * instead of a second hand-tuned copy that can drift from the first.
 *
 * What this does NOT do
 * ──────────────────────
 * It only detects overflow and reports it once via `onOverflow`. Recovery
 * (destroying the local Y.Doc, re-initializing after a cool-down) is
 * store-specific and stays in the caller — org and conversation rooms have
 * different re-init entry points and different cool-down/backoff needs.
 */

import type { WebsocketProvider } from 'y-websocket';

// Matches the window used by both stores' original implementations: two
// abnormal closes within 30s on a doc past the size threshold is treated as
// the overflow circuit-breaker firing, not a single unlucky network blip.
const CONSECUTIVE_1006_WINDOW_MS = 30_000;
const CONSECUTIVE_1006_THRESHOLD = 2;

export interface OverflowDetectorOptions {
	/**
	 * Byte threshold below which a 1006 close is treated as transient
	 * connection instability, not overflow. Callers should derive this from
	 * the room's own per-prefix payload budget (see `wsSizeBudget`'s
	 * `_DEFAULT_HARD_LIMITS_BY_PREFIX`) rather than inventing an independent
	 * number, so the two stay in lockstep.
	 */
	sizeThresholdBytes: number;
	/** Returns the current encoded size of the bound Y.Doc, in bytes. */
	getDocBytes: () => number;
	/**
	 * Called at most once per detector instance, the first time overflow is
	 * detected (either an explicit 1008/oversized close, or the consecutive-
	 * 1006 pattern on a doc past the size threshold).
	 */
	onOverflow: () => void;
	/** Optional logger; defaults to console. */
	logger?: { warn: (...args: unknown[]) => void };
}

export interface OverflowDetectorHandle {
	/**
	 * Attach the close listener to the provider's CURRENT underlying
	 * WebSocket. y-websocket (and the Hocuspocus shim) create a fresh
	 * WebSocket on every (re)connect attempt, so the caller must invoke this
	 * again from its 'connecting' status handler each time — a listener
	 * bound to a prior socket is not carried over automatically.
	 */
	attachToCurrentSocket(): void;
}

/**
 * Creates a detector that tracks consecutive-1006 state for ONE room across
 * however many reconnect attempts happen during this detector's lifetime.
 * Construct a fresh detector per room-session (i.e. per call to the store's
 * init function) — do not share one detector across unrelated rooms.
 */
export function createOverflowDetector(
	provider: WebsocketProvider,
	options: OverflowDetectorOptions,
): OverflowDetectorHandle {
	const { sizeThresholdBytes, getDocBytes, onOverflow } = options;
	const logger = options.logger ?? console;

	let consec1006Count = 0;
	let last1006At = 0;
	let triggered = false;

	const trigger = () => {
		if (triggered) return;
		triggered = true;
		onOverflow();
	};

	return {
		attachToCurrentSocket() {
			if (triggered) return;
			const ws = (provider as unknown as { ws?: WebSocket | null }).ws;
			if (!ws) return;
			ws.addEventListener(
				'close',
				(event: CloseEvent) => {
					if (triggered) return;
					if (event.code === 1008 && /oversized|too large/i.test(event.reason || '')) {
						trigger();
						return;
					}
					if (event.code !== 1006) return;

					// A doc well under the payload budget cannot be the overflow the
					// circuit-breaker exists to catch — repeated 1006s on it are chronic
					// connection instability from something else. Ride those out via the
					// provider's own reconnect/backoff instead of destroying an
					// already-synced local doc the client would just have to re-fetch.
					const docBytes = getDocBytes();
					if (docBytes < sizeThresholdBytes) {
						consec1006Count = 0;
						return;
					}

					const now = Date.now();
					if (now - last1006At < CONSECUTIVE_1006_WINDOW_MS) {
						consec1006Count++;
					} else {
						consec1006Count = 1;
					}
					last1006At = now;
					if (consec1006Count >= CONSECUTIVE_1006_THRESHOLD) {
						logger.warn(
							`[wsOverflowRecovery] ${consec1006Count} consecutive 1006 closes on a ` +
							`${(docBytes / 1024 / 1024).toFixed(2)}MB doc (threshold=${(sizeThresholdBytes / 1024 / 1024).toFixed(2)}MB) ` +
							`— treating as payload overflow`,
						);
						trigger();
					}
				},
				{ once: true },
			);
		},
	};
}
